<script lang="ts">
	import { isMenuActive } from "../../utils/isMenuActive.js";
	import Link from "../Link.svelte";
	import { createEventDispatcher, onMount } from "svelte";
	import type { MenuItem } from "../../../../core/schema/MenuItem.js";
	import arrowDown from "../../../../core/assets/icons/arrow-down.svg?raw";
	import Button from "../Button.svelte";
	import Submenu from "./Submenu.svelte";
	import DesktopSubmenu from "./DesktopSubmenu.svelte";
	import { writable } from "svelte/store";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import translations from "./Menu.translations.json";
	import { getTranslate } from "../../utils/getTranslate.js";

	export let pageSlug: string;
	export let menu: MenuItem[];
	export let desktop = false;

	const translate = getTranslate(translations);

	const dispatch = createEventDispatcher<{
		click: MenuItem;
	}>();
	const activeSubmenu = writable<MenuItem["id"] | null>(null);

	const touchQuery = "(hover: none)";

	let hydrated = false;
	onMount(() => {
		hydrated = true;
	});

	let container: HTMLDivElement;
	let activeId: MenuItem["id"] | null = null;

	function hasSubmenus(items: MenuItem[], id: MenuItem["id"]): boolean {
		return (items.find((item) => item.id === id)?.submenus?.length ?? 0) > 0;
	}

	$: activeItem = menu.find(({ id }) => id === activeId && hasSubmenus(menu, id)) ?? null;

	function handlePointerEnter(event: PointerEvent, id: MenuItem["id"]): void {
		if (event.pointerType !== "touch") {
			activeId = id;
		}
	}

	function handlePointerLeave(event: PointerEvent): void {
		if (event.pointerType !== "touch") {
			activeId = null;
		}
	}

	function handleFocusIn(event: FocusEvent, id: MenuItem["id"]): void {
		if (!(event.target instanceof Element)) {
			return;
		}
		if (event.target.matches(":focus-visible")) {
			activeId = id;
		}
	}

	function handleFocusOut(event: FocusEvent & { currentTarget: HTMLDivElement }): void {
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			activeId = null;
		}
	}

	function closeOnOutsidePointerDown(event: PointerEvent): void {
		if (activeId !== null && !container.contains(event.target as Node)) {
			activeId = null;
		}
	}

	function handleLinkClick(event: MouseEvent, menuItem: MenuItem): void {
		if (desktop && hasSubmenus(menu, menuItem.id) && window.matchMedia(touchQuery).matches) {
			event.preventDefault();
			activeId = activeId === menuItem.id ? null : menuItem.id;
			return;
		}
		dispatch("click", menuItem);
	}

	function toggleSubmenu(event: MouseEvent, id: string): void {
		event.preventDefault();
		activeSubmenu.set($activeSubmenu === id ? null : id);
	}
</script>

<svelte:window on:pointerdown={closeOnOutsidePointerDown} />

<div
	bind:this={container}
	class="mt-6 flex justify-center max-xl:overflow-y-auto max-xl:overflow-x-hidden xl:mt-0 xl:size-full"
	on:pointerleave={handlePointerLeave}
	on:focusout={handleFocusOut}
>
	<ul class="flex w-full flex-col xl:h-full xl:flex-row xl:justify-center">
		{#each menu as menuItem}
			<li
				class="border-lttertiary w-full max-xl:border-b max-xl:first:border-t xl:flex xl:w-auto xl:items-center"
				on:pointerenter={(event) => {
					handlePointerEnter(event, menuItem.id);
				}}
				on:focusin={(event) => {
					handleFocusIn(event, menuItem.id);
				}}
			>
				<div class="relative w-full max-xl:px-6 xl:w-fit">
					<Link
						linkVariant="white"
						noUnderline
						iconGap="mr-0"
						class="!block w-full py-4 text-left text-sm font-medium uppercase tracking-[0.1rem] !text-black transition-colors duration-300 xl:w-fit xl:whitespace-nowrap xl:rounded-lg xl:px-3 xl:py-0 xl:text-center xl:text-xs xl:tracking-[0.125rem] xl:!text-white {hasSubmenus(
							menu,
							menuItem.id,
						)
							? 'max-xl:!pr-10'
							: ''} {isMenuActive(menuItem, pageSlug) ? 'text-black xl:!text-white' : ''}"
						target={menuItem.target}
						href={`/${menuItem.path}`}
						on:click={(event) => {
							handleLinkClick(event, menuItem);
						}}
					>
						{nbspify(menuItem.name)}
					</Link>
					{#if !desktop && hasSubmenus(menu, menuItem.id)}
						<Button
							aria-label={translate("toggleSubmenu", { label: menuItem.name }, true)}
							aria-expanded={$activeSubmenu === menuItem.id}
							class="!absolute inset-y-0 !right-6 h-full justify-center border-none !px-0 py-3 transition-all duration-300 ease-in-out xl:hidden"
							iconGap="mr-0"
							variant="custom"
							icon={arrowDown}
							iconClass="w-5"
							on:click={(event) => {
								toggleSubmenu(event, menuItem.id);
							}}
						/>
					{/if}
				</div>
				{#if !desktop && hasSubmenus(menu, menuItem.id)}
					<Submenu {activeSubmenu} {menuItem} />
				{:else if desktop && !hydrated && hasSubmenus(menu, menuItem.id)}
					<DesktopSubmenu activeItem={menuItem} cssHover />
				{/if}
			</li>
		{/each}
	</ul>

	{#if desktop}
		<DesktopSubmenu {activeItem} />
	{/if}
</div>
